/**
 * Velocity → color maps for Canvas2D (CSS buckets) and WebGL (RGB LUT).
 *
 * Mapping (both backends must match):
 *   t = clamp(‖v‖ · colorScale, 0, 1)
 *
 * All ramps are designed for a near-black stage: the t=0 floor stays
 * readable (relative luminance ≳ 0.12), never “dark purple on black”.
 */

export type ColormapId =
  | "thermal"
  | "plasma"
  | "viridis"
  | "cividis"
  | "turbo"
  | "mono";

/** Catalog / MODEL alias → canonical id */
export const COLORMAP_ALIASES: Record<string, ColormapId> = {
  velocity_thermal: "thermal",
  thermal: "thermal",
  plasma: "plasma",
  viridis: "viridis",
  cividis: "cividis",
  turbo: "turbo",
  mono: "mono",
  grayscale: "mono",
  grey: "mono",
};

export const COLORMAP_IDS: readonly ColormapId[] = [
  "thermal",
  "plasma",
  "viridis",
  "cividis",
  "turbo",
  "mono",
] as const;

export function normalizeColormap(id: string | null | undefined): ColormapId {
  if (!id) return "thermal";
  const key = String(id).toLowerCase().trim();
  return COLORMAP_ALIASES[key] ?? "thermal";
}

const SIZE = 256;

type Rgb = [number, number, number];

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpRgb(a: Rgb, b: Rgb, t: number): Rgb {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

/** Piecewise stops in 0..1 domain → sRGB 0..255 */
function fromStops(t: number, stops: readonly (readonly [number, Rgb])[]): Rgb {
  const x = clamp01(t);
  if (x <= stops[0]![0]) return stops[0]![1];
  for (let i = 1; i < stops.length; i++) {
    const [t1, c1] = stops[i]!;
    const [t0, c0] = stops[i - 1]!;
    if (x <= t1) {
      const u = (x - t0) / Math.max(1e-12, t1 - t0);
      return lerpRgb(c0, c1, u);
    }
  }
  return stops[stops.length - 1]![1];
}

/**
 * Thermal / solar — high floor (sky-indigo), through magenta → orange → gold.
 * Old ramp started near (20,10,40) which vanished on #000.
 */
function sampleThermal(t: number): Rgb {
  return fromStops(t, [
    [0.0, [72, 88, 196]], // bright indigo
    [0.22, [140, 70, 210]], // violet
    [0.45, [230, 70, 120]], // hot pink
    [0.68, [255, 140, 50]], // orange
    [0.85, [255, 210, 70]], // gold
    [1.0, [255, 250, 220]], // warm white
  ]);
}

/** Plasma-like (Matplotlib spirit) — purple→pink→gold, lifted floor */
function samplePlasma(t: number): Rgb {
  return fromStops(t, [
    [0.0, [95, 55, 170]], // lifted vs classic plasma t=0
    [0.2, [145, 45, 175]],
    [0.4, [210, 55, 140]],
    [0.65, [250, 125, 65]],
    [0.85, [255, 205, 75]],
    [1.0, [255, 250, 210]],
  ]);
}

/** Viridis-like — teal/green/yellow with lifted floor for stage contrast */
function sampleViridis(t: number): Rgb {
  return fromStops(t, [
    [0.0, [90, 75, 155]], // lifted purple-blue (classic viridis t=0 is near-black)
    [0.2, [70, 110, 165]],
    [0.45, [40, 165, 150]],
    [0.7, [110, 205, 100]],
    [0.9, [215, 230, 60]],
    [1.0, [253, 245, 120]],
  ]);
}

/** Cividis-ish — colorblind-friendly blue→yellow */
function sampleCividis(t: number): Rgb {
  return fromStops(t, [
    [0.0, [70, 95, 145]],
    [0.25, [80, 110, 145]],
    [0.5, [120, 140, 125]],
    [0.75, [185, 175, 95]],
    [1.0, [250, 235, 110]],
  ]);
}

/** Turbo-like rainbow with lifted ends (no near-black; hot end stays bright) */
function sampleTurbo(t: number): Rgb {
  return fromStops(t, [
    [0.0, [60, 100, 220]],
    [0.15, [50, 170, 235]],
    [0.35, [50, 210, 130]],
    [0.5, [190, 230, 60]],
    [0.65, [255, 190, 50]],
    [0.8, [255, 120, 70]],
    [1.0, [255, 160, 190]], // warm rose, not deep red (readable + bright)
  ]);
}

/** Mono cyan→white — maximum legibility, minimal “pretty” */
function sampleMono(t: number): Rgb {
  return fromStops(t, [
    [0.0, [90, 160, 200]],
    [0.5, [160, 210, 235]],
    [1.0, [245, 250, 255]],
  ]);
}

/**
 * Ensure stage contrast: mix toward a readable slate if the sample is too dark.
 * Relative luminance on black needs roughly L ≳ 0.08 for 2px points.
 */
function ensureFloor(rgb: Rgb): Rgb {
  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const L = relativeLuminance01(r, g, b);
  const minL = 0.1;
  if (L >= minL) return rgb;
  // Blend toward a cool slate that is known-readable on #000.
  const fr = 100;
  const fg = 130;
  const fb = 190;
  // How much floor color to mix: scale with deficit (cap 0.65 so hue survives).
  const mix = Math.min(0.65, (minL - L) / minL);
  return [
    Math.round(rgb[0] * (1 - mix) + fr * mix),
    Math.round(rgb[1] * (1 - mix) + fg * mix),
    Math.round(rgb[2] * (1 - mix) + fb * mix),
  ];
}

function sampleRgb(id: ColormapId, t: number): Rgb {
  let raw: Rgb;
  switch (id) {
    case "plasma":
      raw = samplePlasma(t);
      break;
    case "viridis":
      raw = sampleViridis(t);
      break;
    case "cividis":
      raw = sampleCividis(t);
      break;
    case "turbo":
      raw = sampleTurbo(t);
      break;
    case "mono":
      raw = sampleMono(t);
      break;
    case "thermal":
    default:
      raw = sampleThermal(t);
  }
  return ensureFloor(raw);
}

type Lut = {
  css: string[];
  /** packed RGB 0..1, length SIZE*3 */
  rgb01: Float32Array;
  /** packed bytes RGB, length SIZE*3 — WebGL texture upload */
  rgb8: Uint8Array;
};

const LUT_CACHE = new Map<ColormapId, Lut>();

function buildLut(id: ColormapId): Lut {
  const css: string[] = new Array(SIZE);
  const rgb01 = new Float32Array(SIZE * 3);
  const rgb8 = new Uint8Array(SIZE * 3);
  for (let i = 0; i < SIZE; i++) {
    const [r, g, b] = sampleRgb(id, i / (SIZE - 1));
    const ri = Math.max(0, Math.min(255, r | 0));
    const gi = Math.max(0, Math.min(255, g | 0));
    const bi = Math.max(0, Math.min(255, b | 0));
    css[i] = `rgb(${ri},${gi},${bi})`;
    rgb01[i * 3] = ri / 255;
    rgb01[i * 3 + 1] = gi / 255;
    rgb01[i * 3 + 2] = bi / 255;
    rgb8[i * 3] = ri;
    rgb8[i * 3 + 1] = gi;
    rgb8[i * 3 + 2] = bi;
  }
  return { css, rgb01, rgb8 };
}

function lut(id: ColormapId): Lut {
  let L = LUT_CACHE.get(id);
  if (!L) {
    L = buildLut(id);
    LUT_CACHE.set(id, L);
  }
  return L;
}

/** CSS color for continuous t∈[0,1]. */
export function speedToColor(
  t: number,
  colormap: ColormapId | string = "thermal",
): string {
  const id = normalizeColormap(colormap);
  const idx = Math.max(0, Math.min(SIZE - 1, (clamp01(t) * (SIZE - 1)) | 0));
  return lut(id).css[idx]!;
}

/** RGB 0..1 for continuous t. */
export function speedToRgb01(
  t: number,
  colormap: ColormapId | string = "thermal",
): Rgb {
  const id = normalizeColormap(colormap);
  const idx = Math.max(0, Math.min(SIZE - 1, (clamp01(t) * (SIZE - 1)) | 0));
  const L = lut(id);
  return [L.rgb01[idx * 3]!, L.rgb01[idx * 3 + 1]!, L.rgb01[idx * 3 + 2]!];
}

/** Full 256×RGB8 LUT for WebGL texture (row-major). */
export function colormapLutRgb8(
  colormap: ColormapId | string = "thermal",
): Uint8Array {
  return lut(normalizeColormap(colormap)).rgb8;
}

/** Bucket index 0..BUCKETS-1 for batched Canvas draws */
export const COLOR_BUCKETS = 32;

export function speedToBucket(t: number): number {
  const x = Math.max(0, Math.min(0.999999, t));
  return (x * COLOR_BUCKETS) | 0;
}

export function bucketColor(
  bucket: number,
  colormap: ColormapId | string = "thermal",
): string {
  const t = (bucket + 0.5) / COLOR_BUCKETS;
  return speedToColor(t, colormap);
}

/** Relative luminance (sRGB 0..1 channels) — for tests / contrast checks. */
export function relativeLuminance01(r: number, g: number, b: number): number {
  const lin = (c: number) => {
    const x = clamp01(c);
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
